/**
 * SA-460 (workflow UX sweep spec §6.3, D4): what a step did on THIS record —
 * the drawer's live half. Pure: reads the process read (the bag, the ledger
 * path, the open tasks, the stale and redacted ids), the record's completed
 * tasks (SA-423's history, newest first) and the ledger events, and returns
 * a view model the drawer renders. Nothing here decides what is restricted:
 * the server withheld the value and said so (`redacted`, `redacted_fields`).
 */
import type { OrderEventItem } from '@/types/commerce'
import type { RecordProcessRead, TaskColumnRead, TaskFieldRead, TaskRead } from '@/types/process'
import type { ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import { fieldLabelFor, type ProcessOverviewVM, type ProcessStepVM } from '@/utils/workflowOverview'
import { skippedSteps } from './pathSkips'
import { lowerWords, whenText } from './stepLiveText'

/** The task page's rule: a person's answered row can be re-opened; an outbox row or a wait cannot. */
export const REOPENABLE_KINDS: ReadonlySet<TaskRead['kind']> = new Set<TaskRead['kind']>(['work', 'attention'])

export interface LiveField {
  id: string
  label: string
  type: string
  columns: TaskColumnRead[] | null
  value: unknown
  /** Withheld by the server: the value is null because the viewer may not see it. */
  redacted: boolean
  /** From before the latest revision or re-open (`stale_fields`). */
  stale: boolean
}
export type StepLiveStatus =
  | { kind: 'done'; at: string; seatLabel: string | null; exit: string | null; targetLabel: string | null; rounds: number }
  | { kind: 'current'; seatLabel: string | null; dueAt: string | null; overdue: boolean; waitingFor: string | null }
  | { kind: 'skipped'; fromLabel: string; exit: string; targetLabel: string }
  | { kind: 'pending' }
  | { kind: 'closed_before' }
export interface StepLiveTask {
  id: string
  kind: TaskRead['kind']
  status: string
  assigneeId: string | null
  assigneeName: string | null
  completedByName: string | null
  completedAt: string | null
  /** SA-481: the newest done row's server answer (`TaskRead.can_reopen`) AND the drawer's state rules. */
  canReopen: boolean
  /** SA-480: the viewer can open this task's page (`TaskRead.can_open`). */
  canOpen: boolean
}
export interface StepLiveRound { taskId: string; kind: TaskRead['kind']; exit: string | null; completedAt: string | null; completedByName: string | null }
export interface StepLiveVM {
  stepId: string
  stepTitle: string
  status: StepLiveStatus
  /** The ledger visits to the step (the path), for every status — a step is re-done by re-open, never by a second task row. */
  rounds: number
  recorded: LiveField[]
  saw: LiveField[] | null
  task: StepLiveTask | null
  history: StepLiveRound[]
  events: OrderEventItem[]
}
export interface StepLiveContext {
  vm: ProcessOverviewVM
  definition: ProcessWorkflowDefinitionJson
  /** id → display name, the page's assignee map (SA-330: never render an id). */
  assigneeNames?: Record<string, string>
}

/** English-first, as `workflowOverview.localizedText` reads a label (not exported there). */
const enText = (map: Record<string, string> | null | undefined, fallback: string): string =>
  map?.en ?? Object.values(map ?? {})[0] ?? fallback

function columnsOf(def: ProcessWorkflowDefinitionJson, id: string): TaskColumnRead[] | null {
  const cols = def.fields[id]?.columns
  if (!cols) return null
  return Object.entries(cols).map(([cid, c]) => ({
    id: cid, label: enText(c.label, lowerWords(cid)), type: c.type, options: c.options ?? null, optional: c.optional ?? false, help: null,
  }))
}

/** This step's completed rows, newest first: the API serves them so (SA-423); a stable sort on `completed_at` keeps ties in served order. */
export function doneRowsAt(doneTasks: TaskRead[], stepId: string): TaskRead[] {
  return doneTasks.filter((t) => t.step_id === stepId).sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''))
}

const withheld = (record: RecordProcessRead, id: string) => record.redacted_fields?.includes(id) ?? false

function fromTaskField(record: RecordProcessRead, f: TaskFieldRead): LiveField {
  return {
    id: f.id, label: f.label, type: f.type, columns: f.columns ?? null,
    value: f.redacted === true ? null : f.value,
    redacted: f.redacted === true || withheld(record, f.id),
    stale: record.stale_fields.includes(f.id),
  }
}

function fromBag(record: RecordProcessRead, def: ProcessWorkflowDefinitionJson, id: string): LiveField {
  const redacted = withheld(record, id)
  return {
    id, label: fieldLabelFor(def, id), type: def.fields[id]?.type ?? 'string', columns: columnsOf(def, id),
    value: redacted ? null : (id in record.fields ? record.fields[id] : null),
    redacted, stale: record.stale_fields.includes(id),
  }
}

/** The step's produces as recorded: from the first of `rows` (newest first) whose frozen fields hold the id, else the bag. A wait's rows carry no fields, so its produces always read the bag (SA-423: a wait freezes nothing). */
export function recordedFields(record: RecordProcessRead, definition: ProcessWorkflowDefinitionJson, step: ProcessStepVM, rows: TaskRead[]): LiveField[] {
  return step.produces.map(({ id }) => {
    for (const t of rows) {
      const f = t.result?.fields.find((x) => x.id === id)
      if (f) return fromTaskField(record, f)
    }
    return fromBag(record, definition, id)
  })
}

export function projectStepLive(
  record: RecordProcessRead, doneTasks: TaskRead[], events: OrderEventItem[], stepId: string, ctx: StepLiveContext,
): StepLiveVM | null {
  const step = ctx.vm.steps.find((s) => s.id === stepId)
  if (!step) return null
  const rows = doneRowsAt(doneTasks, stepId)
  const answering = rows.filter((t) => t.result !== null && t.result !== undefined && t.kind !== 'wait')
  const visits = record.path.filter((p) => p.step === stepId)
  const cs = record.current_step
  let status: StepLiveStatus
  if (cs && cs.id === stepId) {
    status = {
      kind: 'current', seatLabel: cs.seat_label, dueAt: cs.due_at, overdue: cs.overdue,
      waitingFor: cs.waiting_for_event ? lowerWords(cs.waiting_for_event) : cs.wait_until ? `until ${whenText(cs.wait_until) ?? cs.wait_until}` : null,
    }
  } else if (visits.length) {
    const last = visits[visits.length - 1]
    status = {
      kind: 'done', at: last.occurred_at, seatLabel: rows[0]?.seat_label ?? step.who, exit: last.exit,
      targetLabel: last.exit ? step.exits.find((e) => e.name === last.exit)?.targetLabel ?? null : null, rounds: visits.length,
    }
  } else {
    // SA-1037: an exit jumped over this step (`reused`: 5 → 7, over 6).
    const skip = skippedSteps(ctx.vm, record.path, cs?.id ?? null).get(stepId)
    const from = skip ? ctx.vm.steps.find((s) => s.id === skip.fromStep) : undefined
    if (skip) status = { kind: 'skipped', fromLabel: from ? `${from.number} ${from.title}` : skip.fromStep, exit: skip.exit, targetLabel: skip.targetLabel }
    else if (record.status === 'closed') status = { kind: 'closed_before' }
    else status = { kind: 'pending' }
  }

  const open = record.open_tasks.find((t) => t.step_id === stepId) ?? null
  const newest = rows[0] ?? null
  const row = open ?? newest
  const task: StepLiveTask | null = row ? {
    id: row.id, kind: row.kind, status: row.status, assigneeId: row.assignee_id,
    assigneeName: row.assignee_id ? ctx.assigneeNames?.[row.assignee_id] ?? null : null,
    completedByName: row.result?.completed_by_name ?? null, completedAt: row.completed_at,
    canReopen: !open && newest !== null && REOPENABLE_KINDS.has(newest.kind) && record.status === 'running' && newest.can_reopen,
    canOpen: row.can_open,
  } : null
  const seen = answering[0]?.result?.needs ?? []
  return {
    stepId, stepTitle: step.title, status, rounds: visits.length,
    recorded: recordedFields(record, ctx.definition, step, answering),
    saw: seen.length ? seen.map((f) => fromTaskField(record, f)) : null,
    task,
    history: rows.map((t) => ({ taskId: t.id, kind: t.kind, exit: t.result_exit, completedAt: t.completed_at, completedByName: t.result?.completed_by_name ?? null })),
    events: events.filter((e) => e.payload?.step === stepId).sort((a, b) => a.occurredAt.localeCompare(b.occurredAt)),
  }
}
